import { buildEmbedCode } from '@flexbyt/shared';
import { cx } from '@flexbyt/widget-preview';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { Breadcrumbs } from '../components/site/Breadcrumbs.js';
import { WidgetBuilder } from '../components/tools/WidgetBuilder.js';
import { SiteLink, buttonClass } from '../components/ui/button.js';
import { Measure, Section } from '../components/ui/layout.js';
import { Eyebrow, Heading, MarkPhrase, Text } from '../components/ui/type.js';
import { sourcePagePath } from '../content/integrations.js';
import { PLATFORM_CONTENT, SEO_PLATFORMS } from '../content/platforms.js';
import { CONNECT_SOURCES } from '../content/sources.js';
import {
    WIDGET_BUILDER_PATH,
    WIDGET_CONTENT,
    generatorPath,
    widgetPath,
} from '../content/widgets.js';
import { breadcrumbSchema, faqSchema, jsonLd, seo } from '../lib/seo.js';

/**
 * A free widget generator.
 *
 * ===========================================================================
 * The tool is the page
 * ===========================================================================
 * It sits directly under a two-line heading, above everything else. Somebody
 * arriving from a search for "review masonry generator" wants the generator,
 * and a page that makes them scroll past nine hundred words of preamble to
 * reach it has answered a different question from the one they asked.
 *
 * The explanatory content is underneath, where it earns the ranking without
 * getting in the way — which is the only arrangement that serves both the
 * visitor and the search engine, because they want the same thing here.
 *
 * ===========================================================================
 * Two doors, one tool
 * ===========================================================================
 * `/tools/review-masonry-generator` opens on masonry with the layout fixed:
 * somebody who typed that query has already decided, and offering them a
 * chooser is asking them to answer it twice.
 * `/tools/review-widget-generator` opens with the layout picker, for somebody
 * arriving from the homepage who has not.
 *
 * Same builder, one prop apart.
 */
export const Route = createFileRoute('/tools/$slug')({
  loader: ({ params }) => {
    if (params.slug === 'review-widget-generator') {
      return { widget: null as null | (typeof WIDGET_CONTENT)[number] };
    }

    const widget = WIDGET_CONTENT.find(
      (entry) => generatorPath(entry) === `/tools/${params.slug}`
    );
    // A 404 rather than a redirect to the general builder:
    // `/tools/review-timeline-generator` is not a typo, it is a tool that does
    // not exist, and saying so is more useful than landing somewhere else.
    if (!widget) throw notFound();
    return { widget };
  },
  head: ({ loaderData, params }) => {
    const widget = loaderData?.widget;

    const { meta, links } = widget
      ? seo({
          title: `Free ${widget.seoName.toLowerCase()} review widget generator`,
          description: `Build a ${widget.seoName.toLowerCase()} review widget for your website free — no account needed. ${widget.intro}`,
          path: `/tools/${params.slug}`,
        })
      : seo({
          title: 'Free review widget generator',
          description:
            'Build a review widget for your website free — carousel, masonry, marquee, grid, list or popup. No account needed to try it.',
          path: WIDGET_BUILDER_PATH,
        });

    return {
      meta,
      links,
      scripts: [
        {
          type: 'application/ld+json',
          children: jsonLd(
            breadcrumbSchema([
              { name: 'Home', path: '/' },
              { name: 'Free tools', path: '/tools' },
              {
                name: widget
                  ? `${widget.seoName} generator`
                  : 'Review widget generator',
                path: widget ? generatorPath(widget) : WIDGET_BUILDER_PATH,
              },
            ])
          ),
        },
        ...(widget
          ? [
              {
                type: 'application/ld+json',
                children: jsonLd(faqSchema(widget.faqs)),
              },
            ]
          : []),
      ],
    };
  },
  component: Generator,
});

function Generator() {
  const { widget } = Route.useLoaderData();

  const title = widget
    ? `Free ${widget.seoName.toLowerCase()} review widget generator`
    : 'Free review widget generator';

  /*
    The layout's name when this page is about one, and "Free" when it is the
    general generator — in both cases the word that says what makes this page
    different from the other six.
  */
  const markPhrase = widget ? widget.seoName.toLowerCase() : 'Free';

  const path = widget ? generatorPath(widget) : WIDGET_BUILDER_PATH;

  return (
    <>
      {/* ------------------------------------------------ The tool, first */}
      <Section size="tight" className="pb-0">
        <Breadcrumbs
          trail={[
            { name: 'Home', path: '/' },
            { name: 'Free tools', path: '/tools' },
            { name: widget ? widget.seoName : 'Widget generator', path },
          ]}
        />

        <Measure className="mt-8">
          <Eyebrow>Free tool</Eyebrow>
          <Heading level={1} size="display" className="mt-3">
            <MarkPhrase text={title} phrase={markPhrase} />
          </Heading>
          <Text className="mt-4">
            {widget
              ? `${widget.intro} Change it below — no account needed until you want to use it.`
              : 'Pick a layout, make it look right, and take it away. Nothing to install and no account needed until you want to put it on your site.'}
          </Text>
        </Measure>
      </Section>

      <Section size="tight" className="pt-8">
        <WidgetBuilder
          initialType={widget ? widget.type : 'masonry'}
          allowLayoutChange={!widget}
          surface={widget ? `tool-${widget.type}` : 'tool-general'}
        />
      </Section>

      {/* ------------------------------------------------- How it works */}
      <Section tone="raised" aria-labelledby="steps-heading">
        <Measure>
          <Heading level={2} id="steps-heading">
            What happens after you build one.
          </Heading>
          <Text className="mt-4 max-w-prose">
            The widget above is styled with sample reviews. Putting it on your
            site is three steps, and the styling you just chose comes with you.
          </Text>
        </Measure>

        <ol className="mt-10 grid gap-8 md:grid-cols-3">
          {[
            {
              n: '01',
              title: 'Make an account',
              body: 'Free, no card. What you built here is waiting for you when you get to the widget step.',
            },
            {
              n: '02',
              title: 'Connect your reviews',
              body: 'Paste the address of your Google, Trustpilot, Yelp, Tripadvisor or Airbnb listing, or your app’s store page. Yours replace the samples.',
            },
            {
              n: '03',
              title: 'Paste one line of code',
              body: 'Or install the app if you are on Wix or Webflow. It updates itself as new reviews arrive.',
            },
          ].map((step) => (
            <li key={step.n}>
              <p className="flex items-baseline gap-3">
                <span className="nums text-body-sm font-semibold text-ink-300">
                  {step.n}
                </span>
                <span className="text-title text-ink-950">{step.title}</span>
              </p>
              <p className="mt-2 max-w-prose text-body-sm text-ink-600">
                {step.body}
              </p>
            </li>
          ))}
        </ol>

        {widget ? (
          <div className="mt-10">
            <p className="text-caption font-semibold text-ink-950">
              The code you end up with
            </p>
            <pre className="mt-2 max-w-2xl overflow-x-auto rounded-lg bg-ink-950 p-4 text-micro leading-relaxed text-white/85">
              <code>
                {buildEmbedCode({
                  widgetType: widget.type,
                  embedId: 'your-site-id',
                  platform: 'other',
                })}
              </code>
            </pre>
          </div>
        ) : null}
      </Section>

      {/* --------------------------------------------------- Explanatory */}
      {widget ? (
        <>
          <Section aria-labelledby="uses-heading">
            <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] lg:gap-16">
              <div>
                <Heading level={2} id="uses-heading">
                  When a {widget.seoName.toLowerCase()} is the right choice.
                </Heading>
                <Text className="mt-4 max-w-prose">{widget.bestFor}</Text>
                <div className="mt-8 border-l-2 border-ink-200 pl-5">
                  <p className="text-caption font-semibold text-ink-950">
                    Pick something else when…
                  </p>
                  <p className="mt-2 max-w-prose text-body-sm text-ink-600">
                    {widget.insteadWhen}
                  </p>
                </div>
              </div>

              <ul className="space-y-6">
                {widget.useCases.map((useCase, index) => (
                  <li key={useCase.title} className="flex gap-5">
                    <span className="nums shrink-0 text-body-sm font-semibold text-ink-300">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <div>
                      <p className="text-title text-ink-950">{useCase.title}</p>
                      <p className="mt-1.5 max-w-prose text-body-sm text-ink-600">
                        {useCase.body}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </Section>

          <Section tone="raised" aria-labelledby="faq-heading">
            <Measure>
              <Heading level={2} id="faq-heading">
                Questions.
              </Heading>
            </Measure>
            <dl className="mt-10 max-w-text">
              {widget.faqs.map((faq, index) => (
                <div
                  key={faq.q}
                  className={cx(index > 0 && 'mt-8 border-t border-ink-150 pt-8')}
                >
                  <dt className="text-title text-ink-950">{faq.q}</dt>
                  <dd className="mt-2.5 text-body text-ink-600">{faq.a}</dd>
                </div>
              ))}
            </dl>
          </Section>
        </>
      ) : null}

      {/* ------------------------------------------------ Internal links */}
      <Section aria-labelledby="more-heading">
        <Heading level={2} id="more-heading" size="title">
          {widget ? 'Other free generators' : 'Generators for one layout'}
        </Heading>
        <ul className="mt-5 flex flex-wrap gap-2">
          {WIDGET_CONTENT.filter(
            (entry) => !widget || entry.slug !== widget.slug
          ).map((entry) => (
            <li key={entry.slug}>
              <SiteLink
                to={generatorPath(entry)}
                className={buttonClass({ variant: 'secondary', size: 'sm' })}
              >
                {entry.seoName} generator
              </SiteLink>
            </li>
          ))}
        </ul>

        <div className="mt-10 grid gap-8 sm:grid-cols-2">
          <div>
            <p className="text-caption font-semibold text-ink-950">
              Where your reviews come from
            </p>
            <ul className="mt-3 flex flex-wrap gap-x-5 gap-y-2">
              {CONNECT_SOURCES.map((source) => (
                <li key={source.id}>
                  <SiteLink
                    to={sourcePagePath(source.id)}
                    className="text-body-sm text-ink-600 underline-offset-[3px] hover:text-ink-950 hover:underline"
                  >
                    {source.name}
                  </SiteLink>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="text-caption font-semibold text-ink-950">
              Putting it on your site
            </p>
            <ul className="mt-3 flex flex-wrap gap-x-5 gap-y-2">
              {SEO_PLATFORMS.map((platform) => (
                <li key={platform}>
                  <SiteLink
                    to={`/for/${PLATFORM_CONTENT[platform].slug}`}
                    className="text-body-sm text-ink-600 underline-offset-[3px] hover:text-ink-950 hover:underline"
                  >
                    {PLATFORM_CONTENT[platform].name}
                  </SiteLink>
                </li>
              ))}
              {widget ? (
                <li>
                  <SiteLink
                    to={widgetPath(widget)}
                    className="text-body-sm text-ink-600 underline-offset-[3px] hover:text-ink-950 hover:underline"
                  >
                    About the {widget.seoName.toLowerCase()}
                  </SiteLink>
                </li>
              ) : null}
            </ul>
          </div>
        </div>
      </Section>
    </>
  );
}
